import * as React from 'react'

import {
  VIEW_LABEL_BY_VALUE,
  VIEW_OPTIONS,
  VIEW_VALUE_BY_LABEL,
} from './helpers'
import { useMomentumScoreGoal, useViewToggleFitsSegmentedControl, } from './hooks'

import MomentumScoreGoalModal from '../../Teacher/components/progress_reports/shared/momentum_score_goal_modal'
import {
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  SMALL_ICON_BASE_SRC,
  SegmentedControl,
  Tooltip,
} from '../../Shared/index'
import { DropdownObjectInterface } from '../../Staff/interfaces/evidenceInterfaces'

export const useMomentumScoreGoalModal = (storageKey: string, displaySnackbar: (text: string) => void) => {
  const [momentumScoreGoal, setMomentumScoreGoal] = useMomentumScoreGoal(storageKey)
  const [showGoalModal, setShowGoalModal] = React.useState(false)

  function openGoalModal() { setShowGoalModal(true) }

  function closeGoalModal(snackbarMessage?: string) {
    setShowGoalModal(false)
    if (typeof snackbarMessage === 'string') displaySnackbar(snackbarMessage)
  }

  const goalModal = showGoalModal && (
    <MomentumScoreGoalModal
      closeModal={closeGoalModal}
      isAdminContext={true}
      momentumScoreGoal={momentumScoreGoal}
      setMomentumScoreGoal={setMomentumScoreGoal}
    />
  )

  return { momentumScoreGoal, openGoalModal, goalModal }
}

export const ReportViewToggle = ({ activeView, dropdownId, onChangeView }: {
  activeView: string,
  dropdownId: string,
  onChangeView: (view: string) => void,
}) => {
  const fitsSegmentedControl = useViewToggleFitsSegmentedControl()

  function handleViewToggle(e: React.MouseEvent<HTMLButtonElement>) {
    // SegmentedControl sets button.value to the label, so map back to our internal value
    const value = VIEW_VALUE_BY_LABEL[(e.currentTarget as HTMLButtonElement).value]
    if (value) onChangeView(value)
  }

  function handleViewDropdownChange(opt: DropdownObjectInterface) { onChangeView(opt.value) }

  if (fitsSegmentedControl) {
    const buttons = VIEW_OPTIONS.map((option) => ({ label: option.label, onClick: handleViewToggle }))
    return (
      <SegmentedControl
        activeTab={VIEW_LABEL_BY_VALUE[activeView]}
        buttons={buttons}
        label="Report view:"
        size="small"
      />
    )
  }
  return (
    <DropdownInput
      ariaLabel="Select view"
      handleChange={handleViewDropdownChange}
      id={dropdownId}
      isSearchable={false}
      options={VIEW_OPTIONS}
      value={VIEW_OPTIONS.find((o) => o.value === activeView)}
    />
  )
}

const handleClickDownload = () => { window.print() }

export const ReportHeaderButtons = ({ guideUrl, onClickChangeGoal }: {
  guideUrl: string,
  onClickChangeGoal: () => void,
}) => (
  <div className="header-buttons">
    <a className="quill-button outlined small grey focus-on-light" href={guideUrl} rel="noopener noreferrer" target="_blank">
      <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`} />
      <span>Guide</span>
    </a>
    <button className="quill-button outlined small grey focus-on-light" onClick={onClickChangeGoal} type="button">
      <img alt="" src={`${SMALL_ICON_BASE_SRC}/pencil.svg`} />
      <span>Change goal</span>
    </button>
    <button className="quill-button outlined small grey focus-on-light" onClick={handleClickDownload} type="button">
      <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />
      <span>Download</span>
    </button>
  </div>
)

export const SelectTeacherPrompt = ({ availableTeachers, applyTeacherSelection }: {
  availableTeachers: any[],
  applyTeacherSelection: (teachers: any[]) => void,
}) => {
  function handleSelectSingleTeacher(teacherOption) {
    if (!teacherOption) return
    applyTeacherSelection([teacherOption])
  }

  return (
    <div className="empty-state teacher-prompt">
      <h2>Select a teacher to view individual student performance</h2>
      <div className="single-teacher-dropdown-wrapper">
        <DropdownInput
          ariaLabel="Select a teacher"
          className="single-teacher-dropdown bordered small focus-on-light"
          handleChange={handleSelectSingleTeacher}
          isSearchable={true}
          options={availableTeachers || []}
          placeholder="Select a teacher..."
          value={null}
        />
      </div>
    </div>
  )
}

export const StudentFilterDropdown = ({ disabled, options, value, onChange }: {
  disabled: boolean,
  options: DropdownObjectInterface[],
  value: DropdownObjectInterface,
  onChange: (option: DropdownObjectInterface) => void,
}) => {
  const dropdown = (
    <DropdownInput
      className="student-filter-dropdown bordered small focus-on-light"
      disabled={disabled}
      handleChange={onChange}
      isSearchable={true}
      label="Student"
      options={options}
      value={value}
    />
  )

  if (disabled) {
    return (
      <Tooltip
        tooltipText="Please select a single teacher to filter by student"
        tooltipTriggerText={dropdown}
      />
    )
  }
  return dropdown
}
